import { useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import {
  AppBar,
  Toolbar,
  IconButton,
  Box,
  Menu,
  MenuItem,
  Avatar,
  Typography,
  Tooltip,
  Divider,
  ListItemIcon,
  Stack,
  ToggleButtonGroup,
  ToggleButton,
} from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import LightModeIcon from '@mui/icons-material/LightModeOutlined';
import DarkModeIcon from '@mui/icons-material/DarkModeOutlined';
import LogoutIcon from '@mui/icons-material/Logout';
import RocketLaunchIcon from '@mui/icons-material/RocketLaunchOutlined';
import PersonIcon from '@mui/icons-material/PersonOutline';
import SettingsIcon from '@mui/icons-material/SettingsOutlined';
import { useThemeMode } from '@/theme/ThemeModeContext';
import { useAuthStore } from '@/store/authStore';
import { useBusinessStore } from '@/store/businessStore';
import { businessesApi } from '@/api/businesses';
import { authApi } from '@/api/auth';
import CreateBusinessDialog from '@/components/CreateBusinessDialog';
import { projectColor, initialsOf } from '@/theme/tokens';
import NotificationsBell from '@/components/layout/NotificationsBell';
import OnboardingTour from '@/components/OnboardingTour';

export default function Topbar({
  onMenuClick,
  title,
  titleIcon, // optional element rendered before the title
  view, // 'day' | 'month' for segmented toggle
  onViewChange, // (val) => void ; when provided, shows DAY/MONTH toggle
}) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { mode, toggleMode } = useThemeMode();
  const { user, logout } = useAuthStore();
  const { currentBusinessId, setCurrentBusiness } = useBusinessStore();

  const [profileAnchor, setProfileAnchor] = useState(null);
  const [tourOpen, setTourOpen] = useState(false);
  const [addBusinessOpen, setAddBusinessOpen] = useState(false);

  /*
   * The project switcher anchors to a REF, not to the node the click handed us.
   *
   * `setAnchor(e.currentTarget)` stores one particular DOM node in state and
   * hands it back to MUI on every subsequent render. The chip re-renders
   * whenever the selected business changes — its avatar, colour and label all
   * come from `current` — and once React swaps that element, the node in state
   * is detached. MUI cannot position against a detached node, so it silently
   * falls back to `document.body` and clamps the result to the nearest corner:
   * the menu appeared bottom-left, over the sidebar, nowhere near the chip.
   *
   * A ref read through a function is resolved at positioning time, so the menu
   * measures whatever element is on screen right now and cannot go stale.
   */
  const projectChipRef = useRef(null);
  const [projectOpen, setProjectOpen] = useState(false);

  const { data: businessesData } = useQuery({
    // See hooks/useBusinesses: suffixed keys are separate caches, not
    // namespaces, and this one duplicated the rail's request.
    queryKey: ['businesses'],
    queryFn: () => businessesApi.list(),
  });
  const businesses = businessesData?.data || businessesData || [];
  const current = businesses.find((b) => String(b.id) === String(currentBusinessId));

  const handleLogout = async () => {
    try {
      await authApi.logout();
    } catch {
      /* ignore */
    }
    logout();
    navigate('/login');
  };

  return (
    <AppBar
      position="sticky"
      color="inherit"
      elevation={0}
      sx={{
        // Opaque, slightly translucent background so page content doesn't ghost
        // through the sticky bar while scrolling.
        bgcolor: (t) => (t.palette.mode === 'light' ? 'rgba(236,238,247,0.85)' : 'rgba(14,19,38,0.85)'),
        backdropFilter: 'saturate(180%) blur(10px)',
        borderBottom: (t) => `1px solid ${t.tokens?.cardBorder || 'rgba(0,0,0,0.06)'}`,
      }}
    >
      <Toolbar sx={{ height: 72, gap: 1.5, px: { xs: 2, md: 3 } }}>
        <IconButton edge="start" onClick={onMenuClick} sx={{ display: { lg: 'none' } }}>
          <MenuIcon />
        </IconButton>

        {/*
          Left: the page title.

          This used to have a second mode that rendered ‹ MONTH › month
          navigation, used by the calendar alone — which put the identical
          control twice on one screen, a few pixels apart, one of them next to
          the heading it was navigating and one of them not. Two controls that
          do the same thing make a user check which one they pressed. The
          calendar keeps the pair inside its own card, next to the month label
          they move.
        */}
        <Stack direction="row" alignItems="center" spacing={1.25}>
          {titleIcon}
          <Typography variant="h5" noWrap sx={{ fontWeight: 600 }}>
            {title}
          </Typography>
        </Stack>

        <Box sx={{ flex: 1 }} />

        {/* DAY / MONTH segmented toggle */}
        {onViewChange && (
          <ToggleButtonGroup
            exclusive
            value={view || 'month'}
            onChange={(_, v) => v && onViewChange(v)}
            sx={{ display: { xs: 'none', sm: 'inline-flex' } }}
          >
            <ToggleButton value="day">Day</ToggleButton>
            <ToggleButton value="month">Month</ToggleButton>
          </ToggleButtonGroup>
        )}

        {/* Project selector chip */}
        <Stack
          ref={projectChipRef}
          direction="row"
          alignItems="center"
          spacing={1}
          onClick={() => setProjectOpen(true)}
          sx={{
            px: 1.25,
            py: 0.5,
            borderRadius: 999,
            cursor: 'pointer',
            bgcolor: 'background.paper',
            border: (t) => `1px solid ${t.tokens.cardBorder}`,
            boxShadow: (t) => t.tokens.shadows.card,
            display: { xs: 'none', sm: 'inline-flex' },
          }}
        >
          <Avatar
            src={current?.logo || undefined}
            sx={{
              width: 26,
              height: 26,
              fontSize: 12,
              bgcolor: current
                ? current.profile?.color || current.color || projectColor(current.id || current.name)
                : 'text.disabled',
            }}
          >
            {current ? initialsOf(current.name) : 'A'}
          </Avatar>
          <Typography sx={{ fontWeight: 600, fontSize: 13, color: 'text.primary' }} noWrap>
            {current?.name || t('nav.allProjects')}
          </Typography>
          <ExpandMoreIcon fontSize="small" sx={{ color: 'text.secondary' }} />
        </Stack>
        <Menu
          // Resolved when the menu positions itself, never captured earlier.
          anchorEl={() => projectChipRef.current}
          // The chip is hidden below `sm`; without the guard the menu would
          // open against an element with no box and land in the corner again.
          open={projectOpen && Boolean(projectChipRef.current)}
          onClose={() => setProjectOpen(false)}
          // Drops under the chip, right edges aligned. The chip sits against
          // the window edge and the menu is wider than it, so left-aligning
          // would overhang and be shoved back by the viewport clamp.
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
          transformOrigin={{ vertical: 'top', horizontal: 'right' }}
          PaperProps={{ sx: { minWidth: 220, mt: 1 } }}
        >
          <MenuItem
            selected={!currentBusinessId}
            onClick={() => {
              setCurrentBusiness('');
              setProjectOpen(false);
            }}
          >
            {t('nav.allProjects')}
          </MenuItem>
          <Divider />
          {businesses.map((b) => (
            <MenuItem
              key={b.id}
              selected={String(b.id) === String(currentBusinessId)}
              onClick={() => {
                setCurrentBusiness(b.id);
                setProjectOpen(false);
              }}
              sx={{ pr: 1, '&:hover .biz-settings': { opacity: 1 } }}
            >
              <Avatar
                src={b.logo || undefined}
                sx={{
                  width: 24,
                  height: 24,
                  fontSize: 11,
                  mr: 1.5,
                  bgcolor: b.profile?.color || b.color || projectColor(b.id || b.name),
                }}
              >
                {initialsOf(b.name)}
              </Avatar>
              <Box component="span" sx={{ flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                {b.name}
              </Box>
              {/* Settings shortcut. stopPropagation so the row's own click
                  (switch business) never fires alongside it. */}
              <Tooltip title={`${b.name} settings`} arrow>
                <IconButton
                  className="biz-settings"
                  size="small"
                  onClick={(e) => {
                    e.stopPropagation();
                    setProjectOpen(false);
                    // Switch too, so the app context matches the page opened.
                    setCurrentBusiness(b.id);
                    navigate(`/businesses/${b.id}/settings`);
                  }}
                  sx={{
                    ml: 1, flexShrink: 0, color: 'text.secondary',
                    opacity: { xs: 1, md: String(b.id) === String(currentBusinessId) ? 1 : 0.45 },
                    transition: 'opacity .15s, color .15s',
                    '&:hover': { color: 'primary.main', bgcolor: 'action.hover' },
                  }}
                >
                  <SettingsIcon sx={{ fontSize: 17 }} />
                </IconButton>
              </Tooltip>
            </MenuItem>
          ))}
          <Divider />
          <MenuItem
            onClick={() => {
              setProjectOpen(false);
              setAddBusinessOpen(true);
            }}
          >
            {`+ ${t('nav.addNewBusiness')}`}
          </MenuItem>
        </Menu>

        <CreateBusinessDialog open={addBusinessOpen} onClose={() => setAddBusinessOpen(false)} />

        <NotificationsBell />

        <Tooltip title={t('topbar.toggleTheme')}>
          <IconButton onClick={toggleMode}>
            {mode === 'light' ? <DarkModeIcon /> : <LightModeIcon />}
          </IconButton>
        </Tooltip>

        <Tooltip title={t('topbar.account')}>
          <IconButton onClick={(e) => setProfileAnchor(e.currentTarget)} sx={{ p: 0.5 }}>
            <Avatar sx={{ width: 36, height: 36, bgcolor: 'primary.main' }}>
              {(user?.name || user?.email || 'U').charAt(0).toUpperCase()}
            </Avatar>
          </IconButton>
        </Tooltip>
        <Menu
          anchorEl={profileAnchor}
          open={!!profileAnchor}
          onClose={() => setProfileAnchor(null)}
          PaperProps={{ sx: { width: 240 } }}
        >
          <Box sx={{ px: 2, py: 1 }}>
            <Typography variant="subtitle2">{user?.name || 'User'}</Typography>
            <Typography variant="caption" color="text.secondary" noWrap>
              {user?.email}
            </Typography>
          </Box>
          <Divider />
          {/* The way back into the first-run checklist after skipping it. */}
          <MenuItem
            onClick={() => {
              setProfileAnchor(null);
              setTourOpen(true);
            }}
          >
            <ListItemIcon><RocketLaunchIcon fontSize="small" /></ListItemIcon>
            Getting started
          </MenuItem>
          <MenuItem
            onClick={() => {
              setProfileAnchor(null);
              navigate('/profile');
            }}
          >
            <ListItemIcon><PersonIcon fontSize="small" /></ListItemIcon>
            Profile
          </MenuItem>
          <MenuItem
            onClick={() => {
              setProfileAnchor(null);
              navigate('/settings');
            }}
          >
            <ListItemIcon><SettingsIcon fontSize="small" /></ListItemIcon>
            Settings
          </MenuItem>
          <MenuItem onClick={toggleMode}>
            <ListItemIcon>
              {mode === 'light' ? <DarkModeIcon fontSize="small" /> : <LightModeIcon fontSize="small" />}
            </ListItemIcon>
            {mode === 'light' ? t('topbar.darkMode') : t('topbar.lightMode')}
          </MenuItem>
          <Divider />
          <MenuItem onClick={handleLogout} sx={{ color: 'error.main' }}>
            <ListItemIcon><LogoutIcon fontSize="small" color="error" /></ListItemIcon>
            {t('topbar.logout')}
          </MenuItem>
        </Menu>

        {/* Controlled instance. The self-opening one lives in AppLayout so it
            can appear on first run without the menu being touched. */}
        <OnboardingTour open={tourOpen} onClose={() => setTourOpen(false)} />
      </Toolbar>
    </AppBar>
  );
}
